import { ApiEnvelope, GmailMessage, GmailThread } from '../../domain/types';
import { IGmailClient, HttpClientOptions, ProviderConnectionProbe } from './types';
import { parseEnvelope } from './parseEnvelope';
import { createHttpTransport, type HttpTransport } from './httpTransport';

export class HttpGmailClient implements IGmailClient {
  private readonly transport: HttpTransport;

  constructor(options: HttpClientOptions | string = {}) {
    this.transport = createHttpTransport(options);
  }

  private async request<T>(path: string, options?: RequestInit): Promise<ApiEnvelope<T>> {
    const res = await this.transport.fetch(path, options);
    return parseEnvelope<T>(res, undefined, 'GMAIL_HTTP_ERROR');
  }

  async getMessages(): Promise<ApiEnvelope<GmailThread[]>> {
    return this.request<GmailThread[]>('/api/gmail/messages');
  }

  async getMessage(id: string): Promise<ApiEnvelope<GmailThread | null>> {
    return this.request<GmailThread | null>(`/api/gmail/messages/${encodeURIComponent(id)}`);
  }

  async markRead(id: string): Promise<ApiEnvelope<GmailMessage>> {
    return this.request<GmailMessage>(`/api/gmail/messages/${encodeURIComponent(id)}/read`, {
      method: 'POST',
    });
  }

  async replyToMessage(id: string, text: string): Promise<ApiEnvelope<GmailMessage>> {
    return this.request<GmailMessage>(`/api/gmail/messages/${encodeURIComponent(id)}/reply`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  async generateThreadSummary(
    id: string,
    options?: { force?: boolean },
  ): Promise<ApiEnvelope<{ summary: string; actionItem?: string; refreshed?: boolean }>> {
    return this.request<{ summary: string; actionItem?: string; refreshed?: boolean }>(
      `/api/gmail/messages/${encodeURIComponent(id)}/summary`,
      {
        method: 'POST',
        body: JSON.stringify({ force: options?.force === true }),
      },
    );
  }

  async generateReplyDraft(
    id: string,
    hint?: string,
  ): Promise<ApiEnvelope<{ draft: string; provider?: string; model?: string }>> {
    return this.request<{ draft: string; provider?: string; model?: string }>(
      `/api/gmail/messages/${encodeURIComponent(id)}/reply-draft`,
      {
        method: 'POST',
        body: JSON.stringify({ hint }),
      },
    );
  }

  async testConnection(): Promise<ApiEnvelope<ProviderConnectionProbe>> {
    return this.request<ProviderConnectionProbe>('/api/gmail/connection/test', {
      method: 'POST',
    });
  }
}
